import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchTimeline, type TimelineEvent } from "../../api";
import { formatDate } from "../../utils/date";
import { TagBadge } from "../../components/TagBadge";
import { FilterBtn } from "../../components/FilterBtn";
import { Link } from "react-router-dom";

import { COMPANIES } from "../../constants/companies";

// Timeline uses extended labels for company display
const TIMELINE_COMPANIES = [
  { value: "", label: "全部" },
  ...COMPANIES.filter(c => c.value !== "").map(c => ({
    value: c.value,
    label: c.value === "ant" ? "蚂蚁集团" : c.value === "bytedance" ? "字节跳动" : c.value === "jd" ? "京东金融" : c.label,
  })),
];

const COMPANY_DOT: Record<string, string> = {
  ant: "bg-info", bytedance: "bg-ink-tertiary", meituan: "bg-warning", jd: "bg-negative",
};

const COMPANY_ACCENT: Record<string, string> = {
  ant: "border-l-info dark:border-l-info-dark",
  bytedance: "border-l-ink-tertiary",
  meituan: "border-l-warning dark:border-l-warning-dark",
  jd: "border-l-negative dark:border-l-negative-dark",
};

const SOURCE_TO_COMPANY: Record<string, string> = {
  ant_official: "ant", bytedance_official: "bytedance", meituan_official: "meituan", jd_official: "jd",
};

const COMPANY_ID_TO_CATEGORY: Record<number, string> = { 1: "ant", 2: "bytedance", 3: "meituan", 4: "jd" };

const CATEGORY_LABELS: Record<string, string> = {
  policy_regulation: "政策", product_update: "产品", market_trend: "趋势",
  company_news: "公司", risk_event: "风险", technology: "技术",
};

const RANGE_OPTIONS = [
  { value: 7, label: "7天" },
  { value: 30, label: "30天" },
  { value: 90, label: "90天" },
];

function resolveCompany(event: TimelineEvent): string | undefined {
  if (event.source_code && SOURCE_TO_COMPANY[event.source_code]) return SOURCE_TO_COMPANY[event.source_code];
  if (Array.isArray(event.related_companies) && event.related_companies.length > 0) return COMPANY_ID_TO_CATEGORY[event.related_companies[0]];
  return undefined;
}

export default function TimelinePage() {
  const [company, setCompany] = useState("");
  const [days, setDays] = useState(30);

  const { data: events = [], isLoading } = useQuery<TimelineEvent[]>({
    queryKey: ["timeline", { company, days }],
    queryFn: () => fetchTimeline({ company: company || undefined, days }),
  });

  return (
    <div className="space-y-5">
      {/* Page header */}
      <section className="paper-panel-elevated overflow-hidden">
        <div className="border-b border-border px-5 py-4 sm:px-6">
          <p className="section-kicker">Competitor Timeline</p>
          <div className="mt-2 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div className="max-w-2xl space-y-2">
              <h1 className="text-[22px] font-semibold tracking-tight text-ink sm:text-[24px]">
                竞品时间线
              </h1>
              <p className="text-sm leading-6 text-ink-secondary">
                按企业维度追踪竞品动态，纵览产品、政策和风险事件脉络。
              </p>
            </div>
            {events.length > 0 && (
              <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-2xs font-mono tabular-nums text-ink-tertiary shrink-0">
                {events.length} 条事件
              </span>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-3 sm:px-6">
          {TIMELINE_COMPANIES.map((c) => (
            <FilterBtn key={c.value} active={company === c.value} onClick={() => setCompany(c.value)}>
              {c.value && <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${COMPANY_DOT[c.value] || ""}`} />}
              {c.label}
            </FilterBtn>
          ))}

          <div className="w-px h-3.5 bg-border mx-1 shrink-0" />

          {RANGE_OPTIONS.map((opt) => (
            <FilterBtn key={opt.value} active={days === opt.value} onClick={() => setDays(opt.value)}>
              {opt.label}
            </FilterBtn>
          ))}
        </div>
      </section>

      {/* Loading */}
      {isLoading && (
        <div className="paper-panel overflow-hidden divide-y divide-border">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-2 px-5 py-4 animate-pulse">
              <div className="h-2.5 w-28 rounded bg-surface-sunken" />
              <div className="h-3.5 w-3/4 rounded bg-surface-sunken" />
              <div className="h-3 w-2/3 rounded bg-surface-sunken" />
            </div>
          ))}
        </div>
      )}

      {/* Empty */}
      {!isLoading && events.length === 0 && (
        <div className="paper-panel px-6 py-16 text-center">
          <p className="text-sm text-ink-secondary">暂无事件数据</p>
          <p className="mt-2 text-xs text-ink-tertiary">可尝试放宽时间范围或切换企业筛选。</p>
        </div>
      )}

      {/* Timeline */}
      {!isLoading && events.length > 0 && (
        <div className="relative">
          <div className="absolute left-[26px] sm:left-[34px] top-0 bottom-0 w-px bg-border" />
          <div className="space-y-0">
            {events.map((event) => {
              const companyCode = resolveCompany(event);
              const accentCls = companyCode ? COMPANY_ACCENT[companyCode] : "border-l-border";
              const dotCls = companyCode ? COMPANY_DOT[companyCode] : "bg-border-strong";

              return (
                <div key={event.id} className="relative pl-10 pr-5 py-4 sm:pl-12 sm:pr-6 border-b border-border last:border-b-0">
                  {/* Dot */}
                  <div className={`absolute left-[22px] sm:left-[30px] top-[22px] h-[9px] w-[9px] rounded-full border-2 border-surface-raised ${dotCls}`} />

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-2 text-2xs text-ink-tertiary mb-1.5">
                    <span className="font-mono tabular-nums">
                      {event.published_at ? formatDate(event.published_at) : "—"}
                    </span>
                    {event.source_name && <span>{event.source_name}</span>}
                    {event.category && (
                      <TagBadge
                        label={CATEGORY_LABELS[event.category] ?? event.category}
                        variant="category"
                      />
                    )}
                  </div>

                  {/* Title */}
                  <Link
                    to={`/article/${event.id}`}
                    className={`block text-[14px] font-semibold leading-6 text-ink transition-colors hover:text-primary-600 dark:hover:text-primary-400 border-l-[3px] pl-3 ${accentCls}`}
                  >
                    {event.title}
                  </Link>

                  {/* Summary */}
                  {event.summary && (
                    <p className="text-sm text-ink-secondary mt-1.5 leading-6 line-clamp-2 pl-3">
                      {event.summary}
                    </p>
                  )}

                  {/* Keywords */}
                  {event.keywords && event.keywords.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-2 pl-3">
                      {event.keywords.slice(0, 3).map((kw) => (
                        <TagBadge key={kw} label={kw} variant="keyword" />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
